import type { JSX } from 'react';
import { useReducer } from 'react';

import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

import { useGetWebhookLimitsQuery } from '../services/webhooks.api';
import type { Webhook, WebhookCreateRequest } from '../types/webhook';
import { detectWebhookPlatform } from '../utils/detectWebhookPlatform';

interface SubProjectOption {
  id: number;
  name: string;
}

interface WebhookFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Create-only from alerts flow: call API and handle errors in the parent. */
  onSubmit: (data: WebhookCreateRequest) => Promise<void>;
  webhook?: Webhook | null;
  subProjects?: SubProjectOption[];
  isLoading?: boolean;
  error?: string | null;
  projectId?: number;
}

const EMPTY_SUB_PROJECTS: SubProjectOption[] = [];

interface WebhookFormState {
  enabled: boolean;
  name: string;
  nameError: string | null;
  subProjectId: number | null;
  url: string;
  urlError: string | null;
}

type WebhookFormAction =
  | { type: 'nameChanged'; value: string; error: string | null }
  | { type: 'urlChanged'; value: string; error: string | null }
  | { type: 'enabledChanged'; value: boolean }
  | { type: 'subProjectIdChanged'; value: number | null }
  | { type: 'nameErrorChanged'; value: string | null }
  | { type: 'urlErrorChanged'; value: string | null };

const createWebhookFormState = (webhook: Webhook | null): WebhookFormState => ({
  enabled: webhook?.enabled ?? true,
  name: webhook?.name ?? '',
  nameError: null,
  subProjectId: webhook?.subProjectId ?? null,
  url: webhook?.url ?? '',
  urlError: null,
});

const webhookFormReducer = (state: WebhookFormState, action: WebhookFormAction): WebhookFormState => {
  switch (action.type) {
    case 'nameChanged':
      return {
        ...state,
        name: action.value,
        nameError: action.error,
      };
    case 'urlChanged':
      return {
        ...state,
        url: action.value,
        urlError: action.error,
      };
    case 'enabledChanged':
      return {
        ...state,
        enabled: action.value,
      };
    case 'subProjectIdChanged':
      return {
        ...state,
        subProjectId: action.value,
      };
    case 'nameErrorChanged':
      return {
        ...state,
        nameError: action.value,
      };
    case 'urlErrorChanged':
      return {
        ...state,
        urlError: action.value,
      };
    default:
      return state;
  }
};

const getNameError = (value: string): string | null => {
  if (!value.trim()) {
    return 'Nombre es requerido';
  }
  if (value.length > 255) {
    return 'Nombre no puede exceder 255 caracteres';
  }
  return null;
};

const getUrlError = (value: string): string | null => {
  if (!value.trim()) {
    return 'URL es requerida';
  }

  try {
    const parsed = new URL(value);
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return 'URL debe usar protocolo HTTP o HTTPS';
    }
  } catch {
    return 'URL inválida';
  }

  if (value.length > 2048) {
    return 'URL no puede exceder 2048 caracteres';
  }

  return null;
};

const WebhookFormDialog = ({
  open,
  onClose,
  onSubmit,
  webhook = null,
  subProjects = EMPTY_SUB_PROJECTS,
  isLoading = false,
  error = null,
  projectId = undefined,
}: WebhookFormDialogProps): JSX.Element => {
  const isEdit = !!webhook;
  const formKey = webhook ? `edit-${webhook.id}` : `create-${String(open)}`;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <WebhookFormDialogContent
        key={formKey}
        error={error}
        isEdit={isEdit}
        isLoading={isLoading}
        onClose={onClose}
        onSubmit={onSubmit}
        open={open}
        projectId={projectId}
        subProjects={subProjects}
        webhook={webhook}
      />
    </Dialog>
  );
};

interface WebhookFormDialogContentProps {
  error: string | null;
  isEdit: boolean;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (data: WebhookCreateRequest) => Promise<void>;
  open: boolean;
  projectId?: number;
  subProjects: SubProjectOption[];
  webhook: Webhook | null;
}

const WebhookFormDialogContent = ({
  error,
  isEdit,
  isLoading,
  onClose,
  onSubmit,
  open,
  projectId,
  subProjects,
  webhook,
}: WebhookFormDialogContentProps): JSX.Element => {
  const [state, dispatch] = useReducer(webhookFormReducer, webhook, createWebhookFormState);
  const { name, url, enabled, subProjectId, nameError, urlError } = state;

  const { data: limitsData } = useGetWebhookLimitsQuery(
    {
      projectId,
      subProjectId,
    },
    {
      skip: !open || isEdit,
    }
  );

  const limitInfo = limitsData ? { current: limitsData.current, max: limitsData.max } : null;

  const handleSubmit = async (): Promise<void> => {
    const nextNameError = getNameError(name);
    const nextUrlError = getUrlError(url);

    dispatch({ type: 'nameErrorChanged', value: nextNameError });
    dispatch({ type: 'urlErrorChanged', value: nextUrlError });

    if (nextNameError || nextUrlError) {
      return;
    }

    await onSubmit({ name, url, enabled, subProjectId });
  };

  const detectedPlatform = url ? detectWebhookPlatform(url) : null;
  const subProjectSelectValue = subProjectId === null ? 'general' : String(subProjectId);
  const atLimit = limitInfo !== null && limitInfo.current >= limitInfo.max;

  return (
    <DialogContent className="gap-4 sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{isEdit ? 'Editar Webhook' : 'Crear nuevo webhook'}</DialogTitle>
      </DialogHeader>

      <div className="flex flex-col gap-3 pt-1">
        {error ? (
          <p
            className="m-0 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm
                text-destructive"
            role="alert"
          >
            {error}
          </p>
        ) : null}

        {limitInfo && !isEdit ? (
          <div
            className={cn(
              'rounded-lg border px-3 py-2 text-sm',
              atLimit
                ? 'border-warning bg-warning-light text-neutral-900'
                : 'border-border-subtle bg-muted/40 text-muted-foreground'
            )}
          >
            <p className="m-0">
              Webhooks activos:{' '}
              <span className="font-semibold text-foreground">
                {limitInfo.current} / {limitInfo.max}
              </span>
              {atLimit ? (
                <>
                  <br />
                  <strong>Has alcanzado el límite. Deshabilita o elimina un webhook existente.</strong>
                </>
              ) : null}
            </p>
          </div>
        ) : null}

        <div className="space-y-1.5">
          <Label htmlFor="webhook-name">Nombre del Webhook</Label>
          <Input
            id="webhook-name"
            value={name}
            onChange={(event) => {
              const next = event.target.value;
              dispatch({ type: 'nameChanged', value: next, error: next ? getNameError(next) : null });
            }}
            placeholder="Ej: Notificaciones Slack"
            maxLength={255}
            aria-invalid={!!nameError}
            aria-describedby={nameError ? 'webhook-name-error' : 'webhook-name-hint'}
            autoFocus
          />
          <p
            id={nameError ? 'webhook-name-error' : 'webhook-name-hint'}
            className={cn('m-0 text-xs', nameError ? 'text-destructive' : 'text-muted-foreground')}
          >
            {nameError ?? 'Un nombre descriptivo para identificar este webhook'}
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="webhook-url">URL del Webhook</Label>
          <Input
            id="webhook-url"
            value={url}
            onChange={(event) => {
              const next = event.target.value;
              dispatch({ type: 'urlChanged', value: next, error: next ? getUrlError(next) : null });
            }}
            placeholder="https://hooks.slack.com/services/..."
            aria-invalid={!!urlError}
            aria-describedby={urlError ? 'webhook-url-error' : 'webhook-url-hint'}
          />
          <p
            id={urlError ? 'webhook-url-error' : 'webhook-url-hint'}
            className={cn('m-0 text-xs', urlError ? 'text-destructive' : 'text-muted-foreground')}
          >
            {urlError ?? 'Soportamos Google Chat, Slack, Microsoft Teams y webhooks genéricos'}
          </p>
        </div>

        {detectedPlatform ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">Plataforma detectada:</span>
            <span
              className="inline-block rounded-full px-2 py-0.5 text-xs font-semibold text-white"
              style={{ backgroundColor: detectedPlatform.color }}
            >
              {detectedPlatform.name}
            </span>
          </div>
        ) : null}

        {subProjects.length > 0 ? (
          <div className="space-y-1.5">
            <Label htmlFor="webhook-subproject">Proyecto (opcional)</Label>
            <Select
              value={subProjectSelectValue}
              onValueChange={(value) => {
                dispatch({
                  type: 'subProjectIdChanged',
                  value: value === 'general' ? null : Number(value),
                });
              }}
            >
              <SelectTrigger id="webhook-subproject" className="w-full">
                <SelectValue placeholder="General (sin proyecto)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General (sin proyecto)</SelectItem>
                {subProjects.map((sp) => (
                  <SelectItem key={sp.id} value={String(sp.id)}>
                    {sp.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        <div className="flex items-start justify-between gap-3 rounded-lg border border-border-subtle p-3">
          <div className="min-w-0">
            <Label htmlFor="webhook-enabled" className="text-foreground">
              Webhook habilitado
            </Label>
            <p className="m-0 mt-1 text-xs text-muted-foreground">
              {enabled ? 'Las notificaciones serán enviadas' : 'Las notificaciones están pausadas'}
            </p>
          </div>
          <Switch
            id="webhook-enabled"
            checked={enabled}
            onCheckedChange={(checked) => {
              dispatch({ type: 'enabledChanged', value: checked });
            }}
          />
        </div>
      </div>

      <DialogFooter className="border-t-0 bg-transparent p-0 sm:justify-end">
        <Button type="button" variant="outline" disabled={isLoading} onClick={onClose}>
          Cancelar
        </Button>
        <Button
          type="button"
          disabled={isLoading || !name.trim() || !url.trim() || !!nameError || !!urlError || atLimit}
          onClick={() => {
            handleSubmit().catch(() => {});
          }}
          className="gap-2"
        >
          {isLoading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
          {isEdit ? 'Guardar cambios' : 'Crear webhook'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

export default WebhookFormDialog;
